import React, { useEffect, useRef, useState } from 'react';
import { viabilidadeAPI } from '@/lib/api';
import { Loader2, RefreshCw, AlertTriangle, MapPin, Cable, Tag } from 'lucide-react';
import { ServicoChip, HpChip, vendavelNaCategoria, CATEGORIAS_SERVICO } from './servicos';
import { PlanosClaro } from './PlanosClaro';

const soDigitos = (v) => (v || '').replace(/\D/g, '');
const norm = (s) => (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9]+/g, ' ').trim();

const Caixa = ({ children, tom = 'neutro', testid }) => (
  <div className={`rounded-lg border p-3 text-xs space-y-2 ${tom === 'alerta' ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20' : tom === 'ok' ? 'border-purple-200 bg-purple-50/60 dark:border-purple-800 dark:bg-purple-900/10' : 'border-dashed border-gray-300 dark:border-gray-700'}`} data-testid={testid}>
    <p className="font-semibold flex items-center gap-1.5 text-gray-700 dark:text-gray-200"><Cable className="w-3.5 h-3.5 text-red-600" /> Viabilidade Claro (COM CABO)</p>
    {children}
  </div>
);

// Viabilidade dentro da Nova Venda: CEP + número → complementos → serviços do HP escolhido
export const ViabilidadeInline = ({ cep, numero, cidade, uf, complemento, planos, tipoCliente = 'RESIDENCIAL', credito = null, onEscolherPlano = null, onResultado, testid = 'viab-inline' }) => {
  const cepD = soDigitos(cep);
  const numD = soDigitos(numero);
  const pronto = cepD.length === 8 && !!numD && !!cidade && (uf || '').length === 2;
  const assinatura = pronto ? `${cepD}|${numD}|${norm(cidade)}|${(uf || '').toUpperCase()}` : '';

  const [busca, setBusca] = useState({ assinatura: '', carregando: false, res: null });
  const [hpSel, setHpSel] = useState('');
  const [detalhe, setDetalhe] = useState({ hp: '', carregando: false, res: null });
  const [mostrarPlanos, setMostrarPlanos] = useState(false);
  const cache = useRef({});

  const buscar = async () => {
    setBusca({ assinatura, carregando: true, res: null }); setHpSel(''); setDetalhe({ hp: '', carregando: false, res: null }); onResultado?.(null);
    try {
      const res = (await viabilidadeAPI.complementos({ cep: cepD, numero: numD, cidade, uf, origem: 'nova_venda' })).data;
      cache.current[assinatura] = res;
      setBusca({ assinatura, carregando: false, res });
    } catch {
      setBusca({ assinatura, carregando: false, res: { status: 'erro', mensagem: 'Falha ao consultar a viabilidade' } });
    }
  };

  useEffect(() => {
    if (!assinatura) { if (busca.res) { setBusca({ assinatura: '', carregando: false, res: null }); setHpSel(''); setDetalhe({ hp: '', carregando: false, res: null }); onResultado?.(null); } return undefined; }
    if (cache.current[assinatura]) { setBusca({ assinatura, carregando: false, res: cache.current[assinatura] }); return undefined; }
    const t = setTimeout(buscar, 1500);
    return () => clearTimeout(t);
  }, [assinatura]); // eslint-disable-line

  // Escolha automática: único HP ou complemento digitado casa com um da lista
  useEffect(() => {
    const r = busca.res;
    if (!r || r.status !== 'ok' || hpSel) return;
    if (r.total === 1) { setHpSel(r.hps[0].hp); return; }
    const alvo = norm(complemento);
    if (alvo) {
      const achado = r.hps.find((h) => norm(h.complementos) === alvo) || r.hps.find((h) => norm(h.complementos).includes(alvo));
      if (achado) setHpSel(achado.hp);
    }
  }, [busca.res, complemento, hpSel]);

  useEffect(() => {
    const r = busca.res;
    if (!hpSel || !r || r.status !== 'ok') return undefined;
    let ativo = true;
    setDetalhe({ hp: hpSel, carregando: true, res: null });
    (async () => {
      try {
        const d = (await viabilidadeAPI.detalhe({ hp: hpSel, cidade_codigo: r.cidade?.codigo, cidade: r.cidade?.nome, uf: r.cidade?.uf, origem: 'nova_venda' })).data;
        if (!ativo) return;
        setDetalhe({ hp: hpSel, carregando: false, res: d });
        onResultado?.(d.status === 'ok' ? d : null);
      } catch {
        if (ativo) setDetalhe({ hp: hpSel, carregando: false, res: { status: 'erro', mensagem: 'Falha ao consultar os serviços do HP' } });
      }
    })();
    return () => { ativo = false; };
  }, [hpSel]); // eslint-disable-line

  if (cepD.length !== 8) return null;
  if (!pronto) return <Caixa testid={testid}><p className="text-gray-500">Preencha número, cidade e UF para consultar a viabilidade do endereço.</p></Caixa>;
  if (busca.carregando || busca.assinatura !== assinatura) return <Caixa testid={testid}><p className="text-gray-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando complementos na Claro…</p></Caixa>;
  const r = busca.res;
  if (!r || r.status !== 'ok') {
    return (
      <Caixa tom="alerta" testid={testid}>
        <p className="text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {r?.mensagem || 'Viabilidade não consultada'}</p>
        <button type="button" onClick={buscar} className="inline-flex items-center gap-1 font-semibold text-purple-600 hover:underline" data-testid={`${testid}-tentar`}><RefreshCw className="w-3 h-3" /> Tentar de novo</button>
      </Caixa>
    );
  }
  if (r.total === 0) return <Caixa tom="alerta" testid={testid}><p className="text-amber-700 dark:text-amber-300">Nenhum HP com cabo encontrado para este número ({r.logradouros?.[0]?.logradouro || 'logradouro'}, {r.numero}). Confira o número ou o CEP.</p></Caixa>;
  const d = detalhe.res;
  const avisos = planos ? Object.keys(CATEGORIAS_SERVICO).filter((k) => planos[k] && d?.status === 'ok' && vendavelNaCategoria(d.servicos, k) === false) : [];
  return (
    <Caixa tom="ok" testid={testid}>
      <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.logradouros?.[0]?.logradouro}, {r.numero} • {r.cidade?.nome}/{r.cidade?.uf} • <b>{r.total}</b> complemento(s)</p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={hpSel} onChange={(e) => setHpSel(e.target.value)} className="h-8 min-w-[220px] max-w-full rounded-md border bg-white dark:bg-gray-900 px-2 text-xs" data-testid={`${testid}-select`}>
          <option value="">Escolha o complemento…</option>
          {r.hps.map((h) => <option key={h.hp} value={h.hp}>{h.complementos || 'sem complemento'} — HP {h.hp}</option>)}
        </select>
        {detalhe.carregando && <span className="text-gray-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> consultando serviços…</span>}
        {d?.status === 'ok' && <HpChip hp={d.hp} testid={`${testid}-hp`} />}
      </div>
      {d && d.status !== 'ok' && <p className="text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {d.mensagem || 'Não foi possível consultar os serviços'}</p>}
      {d?.status === 'ok' && (
        <div className="space-y-1.5" data-testid={`${testid}-servicos`}>
          <div className="flex flex-wrap gap-1">{d.servicos.map((s) => <ServicoChip key={s.tipo} s={s} testid={`${testid}-servico-${s.tipo}`} />)}</div>
          <p className="text-[11px] text-gray-500">{d.vendaveis.length ? `${d.vendaveis.length} produto(s) vendável(is)` : 'Nenhum produto vendável neste HP'}{d.codificacao ? ` • ${d.codificacao}` : ''} • o HP foi preenchido no endereço</p>
          {avisos.map((k) => (
            <p key={k} className="text-amber-700 dark:text-amber-300 font-semibold inline-flex items-center gap-1" data-testid={`${testid}-aviso-${k}`}><AlertTriangle className="w-3 h-3" /> {CATEGORIAS_SERVICO[k].label} escolhido na venda, mas a Claro marcou como NÃO vendável neste HP.</p>
          ))}
          <details className="pt-1" onToggle={(e) => setMostrarPlanos(e.currentTarget.open)}>
            <summary className="cursor-pointer font-semibold text-purple-700 dark:text-purple-300 inline-flex items-center gap-1" data-testid={`${testid}-planos-toggle`}><Tag className="w-3 h-3" /> Ver planos e preços da Claro nesta cidade</summary>
            {mostrarPlanos && <div className="mt-2"><PlanosClaro uf={r.cidade?.uf} cidade={r.cidade?.nome} servicos={d.servicos} tipoCliente={tipoCliente} credito={credito} onEscolherPlano={onEscolherPlano} compacto testid={`${testid}-planos`} /></div>}
          </details>
        </div>
      )}
    </Caixa>
  );
};
